<template>
  <form action="init.php" method="post" @submit="onSubmit">
    <h4>{{ translation.please_choose_your_date }}</h4>

    <div class="radio">
      <label>
        <input type="radio" name="typ" value="order" :disabled="!props.dates.length" id="optionsRadios1"
          v-model="orderType" />
        <div v-if="isGerman">Öffentliche Termine</div>
        <div v-if="isEnglish">Public Appointments</div>
      </label>
    </div>
    <div class="radio">
      <label>
        <input type="radio" name="typ" value="request" :disabled="!props.groupDates.length" id="optionsRadios2"
          v-model="orderType" @click="updateInternalModelValue" />
        <div v-if="isGerman">Gruppentermine</div>
        <div v-if="isEnglish">Group Appointments</div>
      </label>
    </div>

    <div class="form-group datepicker" v-show="orderType">
      <VueDatePicker name="date-input" id="date" :model-value="date" :no-today="true" locale="getLocale"
        :allowed-dates="allowedDates" :format-locale="getLocale" cancelText="abbrechen" selectText="auswählen"
        @update:model-value="handleDate" inline auto-apply :month-change-on-scroll="false" :enable-time-picker="false"
        :highlight="allowedDates" />
    </div>

    <input type="hidden" name="date" v-model="dateFormatted" />
    <input type="hidden" name="id" v-model="props.id" />

    <div class="row" v-show="date">
      <div class="col-md-6">
        <div class="form-group">
          <label for="times">{{ translation.time }}</label>
          <select v-model="time" class="form-control" name="time" id="time" v-show="timeArray.times.length">
            <option value="0">---</option>
            <option v-bind:key="time.id" v-for="time in timeArray.times">
              {{ time }}
            </option>
          </select>
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label for="persons">{{ translation.persons }}</label>
          <input type="number" v-model="person" :max="getMaxPerson" :min="getMinPerson" name="persons" id="persons"
            class="form-control" />
        </div>
      </div>

      <div class="col-md-12" v-if="verkostung == 1">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" name="verkostung" id="verkostung" value="1" />
          <label class="form-check-label" for="verkostung">
            <div v-if="isGerman">&nbsp;+ 4,00 € Verkostung</div>
            <div v-if="isEnglish">&nbsp;+ 4,00 € Verkostung</div>
          </label>
        </div>
      </div>

      <div class="col-md-12 alert alert-themed" v-show="selectionCompleted && isGerman">
        <b>Ihre Auswahl</b>:<br />
        {{ dateGerman }} {{ time }} Uhr <br />
        {{ person }} Personen
      </div>

      <div class="col-md-12 alert alert-themed" v-show="selectionCompleted && isEnglish">
        <b>Your selection</b>:<br />
        {{ dateEnglish }} {{ time }} o´clock <br />
        {{ person }} persons
      </div>

      <div class="clearfix"></div>
      <div class="col-md-3" style="margin-top: 3rem">
        <button type="submit" class="btn btn-primary" :disabled="canSubmit" @click="submit">
          <div v-if="isGerman">Weiter</div>
          <div v-if="isEnglish">Continue</div>
        </button>
      </div>
    </div>
  </form>
</template>

<script setup>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { ref, computed, onMounted } from "vue";
import { de, enGB } from "date-fns/locale";
import axios from "axios";

const props = defineProps([
  "id",
  "lang",
  "dates",
  "groupDates",
  "personenanzahl",
  "type",
  "times",
  "minPerson",
  "maxPerson",
  "verkostung",
]);

onMounted(async () => {
  const response = await axios.get(`/api/translations.php?lang=${props.lang}`);
  translation.value = response.data;

  if (props.groupDates.length) {
    orderType.value = "request";
  }
  if (props.dates.length) {
    orderType.value = "order";
  }
});

const orderType = ref("order");
const timeArray = ref({ times: [] });
const date = ref();
const time = ref(0);
const person = ref(props.minPerson);
const verkostung = ref(props.verkostung);
const translation = ref([
  { please_choose_your_date: "" },
  { time: "" },
  { persons: "" },
]);

const dateFormatted = computed(() => {
  return date.value ? date.value.toISOString().split("T")[0] : null;
});

const dateGerman = computed(() => {
  return date.value ? date.value.toLocaleDateString("de-DE") : null;
});

const dateEnglish = computed(() => {
  return date.value ? date.value.toLocaleDateString("en-GB") : null;
});

const getLocale = computed(() => {
  return props.lang == 1 ? de : enGB;
});

const canSubmit = computed(() => {
  return !date.value || !time.value || !person.value;
});

// const highlightDates = computed(() => {
//   let dates = getDatesByRadio();

//   return dates.map((date) => {
//     return new Date(date.date);
//   });
// });

function getDatesByRadio() {
  if (orderType.value == "order") {
    return props.dates;
  } else if (orderType.value == "request") {
    return props.groupDates;
  }

  return [];
}

const handleDate = (modelData) => {
  time.value = 0;
  timeArray.value.times = [];
  date.value = modelData;
  let dates = getDatesByRadio();

  // Format date as YYYY-MM-DD in UTC to match server format
  const year = modelData.getFullYear();
  const month = String(modelData.getMonth() + 1).padStart(2, '0');
  const day = String(modelData.getDate()).padStart(2, '0');
  const selectedDateString = `${year}-${month}-${day}`;

  const times = dates.filter((date) => {
    // Compare date strings directly to avoid timezone issues
    return date.date === selectedDateString;
  });

  if (!times.length) return;

  timeArray.value.times = times[0].times;
};

const selectionCompleted = computed(() => {
  return date.value && time.value && person.value;
});

const isGerman = computed(() => {
  return props.lang == 1;
});

const isEnglish = computed(() => {
  return props.lang == 2;
});

const getMinPerson = computed(() => {
  if (orderType.value == "order") {
    return 1;
  } else if (orderType.value == "request") {
    return props.minPerson;
  }
  return props.minPerson;
});

const getMaxPerson = computed(() => {
  if (orderType.value == "order") {
    return props.personenanzahl;
  } else if (orderType.value == "request") {
    return props.maxPerson;
  }
  return props.maxPerson;
});

const allowedDates = computed(() => {
  let dates = getDatesByRadio();

  return dates.map((date) => {
    // Parse date string as local date (YYYY-MM-DD format from server is already in Berlin timezone)
    // This ensures the date picker shows the correct day regardless of user's timezone
    const [year, month, day] = date.date.split('-').map(Number);
    return new Date(year, month - 1, day);
  });
});
</script>
